import type { Combatant, EncounterState, OptInRiderPrompt, PreRollChoicePrompt, ProjectedEncounterState } from "@lancer-vtt/domain";
import { weaponInstanceKey } from "@lancer-vtt/domain";
import { profilePills } from "../attackIntent";
import type { ActionDraft } from "../combatInspector";
import { tipAttrs } from "../../tooltip";
import { AccuracyStepper } from "./AccuracyStepper";

/**
 * **The armed shot's controls** — the pre-roll ammo selector, the opt-in effect riders, the firing
 * profile pills and the ACC / DIFF stepper, each editing the armed draft. Lifted out of
 * {@link SkirmishChooser} (`token-command-menu` 3.1) so the command menu's fire card renders the
 * SAME controls rather than a fork; the markup and class names are the chooser's own. A Barrage
 * shot gets the profile pills and the stepper only: its fire path sends no pre-roll choices.
 */
export function AttackAimControls({
  combatant: c,
  state,
  aim,
  setAim,
}: {
  combatant: Combatant;
  state: EncounterState;
  aim: Extract<ActionDraft, { mode: "skirmish" | "barrage" }>;
  setAim: (a: ActionDraft | null) => void;
}) {
  const weapons = c.base.weapons ?? [];
  const chosenWeapon = weapons.find((w) => weaponInstanceKey(w) === aim.weaponId);
  const projectedProfiles: ProjectedEncounterState = state;
  const pills = chosenWeapon ? profilePills(chosenWeapon, aim.profileId, projectedProfiles.activeProfiles?.[c.id]?.[weaponInstanceKey(chosenWeapon)]) : [];
  // Pre-roll ammo selector — the server-projected `choose`
  // prompts the armed weapon would raise, discovered pre-target (`ProjectedEncounterState.preRollChoices`,
  // `@lancer-vtt/domain`). `state` is declared `EncounterState` here (the widened wire-honest shape
  // only carries the field at runtime — a free structural upcast, since `EncounterState` widens into
  // `ProjectedEncounterState` for free), so this reads as `undefined` (⇒ no selector) for any caller
  // whose state was never server-projected. **LOAD-BEARING
  // KEY MATCH:** looked up by the SAME `weaponInstanceKey(chosenWeapon)` the projection was keyed by
  // server-side (`backend/src/encounterPreRollProjection.ts`'s `projectWithPreRollChoices`). It can't drift:
  // both sides derive the key from the same `NeedsDecisionSignal`.
  const projectedState: ProjectedEncounterState = state;
  const armedPreRollPrompts: PreRollChoicePrompt[] = chosenWeapon
    ? (projectedState.preRollChoices?.[c.id]?.[weaponInstanceKey(chosenWeapon)] ?? [])
    : [];
  // Opt-in effect riders — the server-projected
  // `requiresOptIn` clauses the armed weapon could raise, discovered the SAME way and keyed by
  // the SAME `weaponInstanceKey` as the ammo selector above (`ProjectedEncounterState.optInRiders`,
  // `backend/src/encounterPreRollProjection.ts`'s `projectWithPreRollChoices`) — never string-concatenated,
  // always `r.key` (`"optIn:" + name`, already assembled server-side).
  const armedOptInRiders: OptInRiderPrompt[] = chosenWeapon
    ? (projectedState.optInRiders?.[c.id]?.[weaponInstanceKey(chosenWeapon)] ?? [])
    : [];
  const choices = aim.mode === "skirmish" ? aim.choices : undefined;
  const setChoices = (next: Record<string, string> | undefined) => {
    if (aim.mode === "skirmish") setAim({ ...aim, choices: next });
  };
  const skirmishOnly = aim.mode === "skirmish";

  return (
    <>
      {/* Pre-roll ammo selector — one row per projected
          `choose` prompt the armed weapon would raise, gated on the projection being non-empty.
          Default is unset ("ASK ME" pressed) — opt-in per shot, no sticky/remembered ammo.
          Picking an option records it on the armed draft; `fireAt` (`CombatApp.tsx`)
          threads `aim.choices` straight into `buildAttackIntent`, which drops it entirely when
          empty (the live mid-resolve prompt fallback path, `46561e54`). */}
      {skirmishOnly && armedPreRollPrompts.length > 0 && (
        <div className="skirmish-hud-ammo" role="group" aria-label="ammo select">
          {armedPreRollPrompts.map((prompt) => {
            const picked = choices?.[prompt.promptId];
            return (
              <div key={prompt.promptId} className="skirmish-hud-ammo-row">
                <span className="skirmish-hud-ammo-label">{prompt.prompt.toUpperCase()}</span>
                <div className="skirmish-hud-ammo-opts">
                  <button
                    type="button"
                    className={`skirmish-hud-ammo-opt${picked === undefined ? " is-sel" : ""}`}
                    aria-pressed={picked === undefined}
                    onClick={() => {
                      if (picked === undefined) return;
                      const rest = { ...choices };
                      delete rest[prompt.promptId];
                      setChoices(Object.keys(rest).length > 0 ? rest : undefined);
                    }}
                  >
                    ASK ME
                  </button>
                  {prompt.options.map((opt) => {
                    const sel = picked === opt.id;
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        className={`skirmish-hud-ammo-opt${sel ? " is-sel" : ""}`}
                        aria-pressed={sel}
                        onClick={() => setChoices({ ...choices, [prompt.promptId]: opt.id })}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Opt-in effect riders — one checkbox per projected
          `requiresOptIn` clause the armed weapon could raise (`ProjectedEncounterState.optInRiders`).
          Ticked state is DERIVED from `aim.choices[r.key]` when the player has explicitly touched
          it, falling back to the clause's authored default (`r.default`) otherwise — it is
          never seeded into state on render. Toggling always writes an EXPLICIT `"true"`/`"false"`
          string, never deletes the key: absence means "no deviation from the authored default,"
          a DIFFERENT thing from "the player explicitly unticked it" (see `combatInspector.ts`'s
          `ActionDraft.choices` doc). This block only edits the draft — the fire-time funnel
          (`CombatApp.tsx`'s `resolveSkirmishFireChoices`, called from `fireAt`) is what resolves
          EVERY projected rider to an explicit wire value and strips stale `optIn:` keys left over
          from a previously-armed weapon (the engine is fail-closed: a missing answer DECLINES).
          Per-shot only, no sticky/remembered state across shots — mirrors the ammo selector above
          on purpose; do NOT add localStorage/a ref/any cross-shot carry-over here. */}
      {skirmishOnly && armedOptInRiders.length > 0 && (
        <div className="skirmish-hud-optin" role="group" aria-label="optional effects">
          {armedOptInRiders.map((r) => {
            const ticked = choices?.[r.key] !== undefined ? choices[r.key] === "true" : r.default;
            return (
              <label key={r.key} className="skirmish-hud-check">
                <input
                  type="checkbox"
                  checked={ticked}
                  onChange={(e) =>
                    setChoices({ ...choices, [r.key]: e.target.checked ? "true" : "false" })
                  }
                />
                <span>{r.label.toUpperCase()}</span>
              </label>
            );
          })}
        </div>
      )}

      {pills.length > 0 && (
        <div className="skirmish-hud-profiles">
          {pills.map((p) => (
            <button
              key={p.id}
              type="button"
              className={`skirmish-hud-prof${p.selected ? " is-sel" : ""}`}
              aria-pressed={p.selected}
              disabled={p.locked && !p.selected}
              {...(p.locked ? tipAttrs({ label: p.selected ? "Live mode — set by the weapon's state" : "Locked — the weapon's state sets this mode" }) : {})}
              onClick={() => {
                if (!p.locked) setAim({ ...aim, profileId: p.pid });
              }}
            >
              {p.name}
            </button>
          ))}
        </div>
      )}

      <AccuracyStepper value={aim.accuracy} onChange={(accuracy) => setAim({ ...aim, accuracy })} />
    </>
  );
}
